import { useEffect, useState } from 'react';
import { api, readErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Users, Plus, Trash2, Star, Pencil, Check, Loader2, Compass } from 'lucide-react';
import { toast } from '@/lib/toast';
import PlaceAutocomplete from '@/components/PlaceAutocomplete';
import { RelationBadge, prettyRelation } from '@/components/ProfilePicker';
import RectifyBirthTimeModal from '@/components/RectifyBirthTimeModal';

/**
 * Round 33 — Family & friends section.
 *
 * Embedded on the Profile page. Lists the user's profiles with add /
 * edit / delete / set-default. The user's own self-profile is auto-
 * created from `users.birth_details` on first /api/profiles call
 * (lazy migration). Pre-existing user data is never lost.
 */
export default function ProfilesSection() {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);   // profile object or 'new'
    const [rectifying, setRectifying] = useState(null);   // profile object

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/profiles');
            setProfiles(data.profiles || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setDefault = async (p) => {
        try {
            await api.post(`/profiles/${p.id}/set-default`);
            toast.success(`${p.name} is now the default profile`);
            await load();
        } catch (e) {
            toast.error(readErr(e, 'Failed to set default'));
        }
    };

    const remove = async (p) => {
        if (p.is_default) {
            toast.error('Set another profile as default before deleting this one.');
            return;
        }
        if (!window.confirm(`Delete profile "${p.name}"? Past readings stay tied to this profile.`)) return;
        try {
            await api.delete(`/profiles/${p.id}`);
            toast.success(`${p.name} deleted`);
            await load();
        } catch (e) {
            toast.error(readErr(e, 'Delete failed'));
        }
    };

    return (
        <Card className="bg-elevated border-marigold/15 rounded-sm p-8" data-testid="profiles-section">
            <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                    <Users className="text-marigold" size={20} />
                    <h2 className="font-serif text-2xl text-parchment">Family &amp; friends</h2>
                </div>
                <Button
                    onClick={() => setEditing('new')}
                    data-testid="add-profile-btn"
                    size="sm"
                    className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm h-9 px-4 text-xs uppercase tracking-widest"
                >
                    <Plus size={14} className="mr-1.5" /> Add profile
                </Button>
            </div>
            <p className="text-sm text-slate-400 mb-5">
                Save the birth details of family or friends to ask questions or book consultations on their behalf.
                The default profile is selected by default on the Ask page.
            </p>

            {loading ? (
                <div className="py-8 flex justify-center">
                    <Loader2 className="animate-spin text-marigold" size={18} />
                </div>
            ) : profiles.length === 0 ? (
                <div className="py-6 text-sm text-slate-500" data-testid="profiles-empty">
                    No profiles yet. Click <strong>Add profile</strong> to get started.
                </div>
            ) : (
                <ul className="divide-y divide-marigold/10" data-testid="profiles-list">
                    {profiles.map((p) => (
                        <li key={p.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`profile-row-${p.id}`}>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-parchment font-medium">{p.name}</span>
                                    <RelationBadge relation={p.relation} />
                                    {p.is_default && (
                                        <span className="text-marigold text-[10px] uppercase tracking-wider border border-marigold/40 px-1.5 py-0.5 rounded-sm">
                                            default
                                        </span>
                                    )}
                                    {p.rectified && (
                                        <span className="text-emerald-400 text-[10px] uppercase tracking-wider border border-emerald-400/40 px-1.5 py-0.5 rounded-sm">
                                            rectified
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                    {p.birth_details?.date} · {p.birth_details?.time} · {p.birth_details?.place}
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                {!p.is_default && (
                                    <Button
                                        size="sm" variant="ghost"
                                        onClick={() => setDefault(p)}
                                        data-testid={`set-default-${p.id}`}
                                        className="h-8 px-2 text-xs text-slate-400 hover:text-marigold hover:bg-marigold/5"
                                    >
                                        <Star size={13} className="mr-1" /> Set default
                                    </Button>
                                )}
                                <Button
                                    size="sm" variant="ghost"
                                    onClick={() => setRectifying(p)}
                                    data-testid={`rectify-profile-${p.id}`}
                                    title="Rectify birth time"
                                    className="h-8 px-2 text-xs text-slate-400 hover:text-marigold hover:bg-marigold/5"
                                >
                                    <Compass size={13} className="mr-1" /> Rectify
                                </Button>
                                <Button
                                    size="sm" variant="ghost"
                                    onClick={() => setEditing(p)}
                                    data-testid={`edit-profile-${p.id}`}
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-marigold hover:bg-marigold/5"
                                >
                                    <Pencil size={13} />
                                </Button>
                                <Button
                                    size="sm" variant="ghost"
                                    onClick={() => remove(p)}
                                    disabled={p.is_default}
                                    data-testid={`delete-profile-${p.id}`}
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-rose-400 hover:bg-rose-500/5 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <Trash2 size={13} />
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <ProfileEditDialog
                open={!!editing}
                profile={editing === 'new' ? null : editing}
                hasExisting={profiles.length > 0}
                onClose={() => setEditing(null)}
                onSaved={async () => { setEditing(null); await load(); }}
            />

            <RectifyBirthTimeModal
                open={!!rectifying}
                onOpenChange={(o) => { if (!o) setRectifying(null); }}
                targetProfile={rectifying}
                onSuccess={async () => { await load(); }}
            />
        </Card>
    );
}


function ProfileEditDialog({ open, profile, hasExisting, onClose, onSaved }) {
    const isNew = !profile;
    const [name, setName] = useState('');
    const [relation, setRelation] = useState('other');
    const [bd, setBd] = useState({ date: '', time: '', place: '' });
    const [placeText, setPlaceText] = useState('');
    const [resolved, setResolved] = useState(null);
    const [btc, setBtc] = useState('not_set');
    const [setAsDefault, setSetAsDefault] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            if (profile) {
                setName(profile.name || '');
                setRelation(profile.relation || 'other');
                setBd(profile.birth_details || { date: '', time: '', place: '' });
                setPlaceText(profile.birth_details?.place || '');
                setResolved(profile.birth_details || null);
                setBtc(profile.birth_time_confidence || 'not_set');
            } else {
                setName('');
                setRelation('other');
                setBd({ date: '', time: '', place: '' });
                setPlaceText('');
                setResolved(null);
                setBtc('not_set');
                setSetAsDefault(false);
            }
        }
    }, [open, profile]);

    const save = async () => {
        if (!name.trim()) return toast.error('Name required');
        if (!bd.date || !bd.time) return toast.error('Birth date and time required');
        setSaving(true);
        try {
            // Resolve place if text differs from last resolved selection
            let coords = resolved;
            if (placeText && (!coords || coords.place !== placeText)) {
                try {
                    const { data } = await api.post('/astrology/geocode', { place: placeText });
                    coords = data;
                } catch {
                    setSaving(false);
                    toast.error('Could not resolve place — pick from the dropdown');
                    return;
                }
            }
            if (!coords) { setSaving(false); return toast.error('Birth place required'); }

            const birth_details = {
                date: bd.date,
                time: bd.time,
                place: coords.place,
                lat: coords.lat,
                lon: coords.lon,
                tz: coords.tz,
                birth_timezone: coords.birth_timezone || coords.timezone,
            };

            if (isNew) {
                const { data: created } = await api.post('/profiles', {
                    name: name.trim(),
                    relation,
                    birth_details,
                    birth_time_confidence: btc,
                    set_default: setAsDefault || !hasExisting,
                });
                if (created?.duplicate_warning) {
                    toast.error(created.duplicate_warning.message, { duration: 9000 });
                } else {
                    toast.success(`${name.trim()} added`);
                }
            } else {
                const { data: updated } = await api.patch(`/profiles/${profile.id}`, {
                    name: name.trim(),
                    relation,
                    birth_details,
                    birth_time_confidence: btc,
                });
                if (updated?.duplicate_warning) {
                    toast.error(updated.duplicate_warning.message, { duration: 9000 });
                } else {
                    toast.success(`${name.trim()} updated`);
                }
            }
            onSaved?.();
        } catch (e) {
            toast.error(readErr(e, 'Save failed'));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-surface border-marigold/15 rounded-sm max-w-lg" data-testid="profile-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-parchment">
                        {isNew ? 'Add profile' : `Edit ${profile?.name}`}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label>
                            <Input
                                value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Priya"
                                data-testid="profile-edit-name"
                                className="mt-2 bg-obsidian border-marigold/15 text-parchment rounded-sm h-10"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-400">Relation</Label>
                            <Select value={relation} onValueChange={setRelation}>
                                <SelectTrigger
                                    className="mt-2 bg-obsidian border-marigold/15 text-parchment rounded-sm h-10"
                                    data-testid="profile-edit-relation"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-surface border-marigold/20">
                                    {['self', 'spouse', 'parent', 'child', 'sibling', 'friend', 'other'].map(r => (
                                        <SelectItem key={r} value={r}>{prettyRelation(r)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-400">Birth date</Label>
                            <Input
                                type="date" value={bd.date || ''} onChange={(e) => setBd({ ...bd, date: e.target.value })}
                                data-testid="profile-edit-date"
                                className="mt-2 bg-obsidian border-marigold/15 text-parchment rounded-sm h-10"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-slate-400">Birth time</Label>
                            <Input
                                type="time" value={bd.time || ''} onChange={(e) => setBd({ ...bd, time: e.target.value })}
                                data-testid="profile-edit-time"
                                className="mt-2 bg-obsidian border-marigold/15 text-parchment rounded-sm h-10"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Birth place</Label>
                        <div className="mt-2">
                            <PlaceAutocomplete
                                value={placeText}
                                onTextChange={setPlaceText}
                                onSelect={(loc) => { setResolved(loc); setPlaceText(loc.place); setBd({ ...bd, place: loc.place }); }}
                                testId="profile-edit-place"
                            />
                        </div>
                        {resolved && resolved.place === placeText && (
                            <div className="mt-2 text-xs text-marigold/90">
                                {resolved.lat}, {resolved.lon} · {resolved.tz}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Birth-time confidence</Label>
                        <Select value={btc} onValueChange={setBtc}>
                            <SelectTrigger
                                className="mt-2 bg-obsidian border-marigold/15 text-parchment rounded-sm h-10"
                                data-testid="profile-edit-btc"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-surface border-marigold/20">
                                <SelectItem value="exact">Exact (from birth certificate)</SelectItem>
                                <SelectItem value="approximate">Approximate (within ~30 min)</SelectItem>
                                <SelectItem value="unknown">Unknown</SelectItem>
                                <SelectItem value="not_set">Prefer not to say</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {isNew && hasExisting && (
                        <label className="flex items-center gap-2 text-sm text-slate-300" data-testid="profile-edit-set-default-row">
                            <input
                                type="checkbox"
                                checked={setAsDefault}
                                onChange={(e) => setSetAsDefault(e.target.checked)}
                                className="accent-marigold"
                                data-testid="profile-edit-set-default"
                            />
                            Make this the default profile
                        </label>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    <Button
                        variant="outline" onClick={onClose}
                        className="bg-transparent border-marigold/20 text-slate-300 rounded-sm"
                    >Cancel</Button>
                    <Button
                        onClick={save} disabled={saving}
                        data-testid="profile-edit-save"
                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm"
                    >
                        {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Check size={14} className="mr-2" />}
                        {isNew ? 'Add profile' : 'Save changes'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
